import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from "@mui/material";
import { Form } from "formik";
import { useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, useEffect, useState } from "react";

import { STATES_WITH_AREA_CODES } from "@stw/states";
import { findEarliestStartDate } from "@stw/yup-validators";

import {
  ComplianceRegistrationUsageType,
  GetRegistrationQuery,
  TenDlcExpectedTexts,
  TenDlcOrgType,
  UpdateBrandAndCampaignRegistrationInput,
} from "~frontend/__generated__/graphql";
import { AccentBox } from "~frontend/components/AccentBox";
import { Checkbox } from "~frontend/components/Checkbox";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import { PageHeader } from "~frontend/components/PageHeader";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import {
  FormikDatePicker,
  FormikSelect,
  FormikTextField,
} from "~frontend/components/forms";
import Select, {
  FormikSearchableSelect,
} from "~frontend/components/forms/FormSelect";
import Alert from "~frontend/components/icons/Alert";
import CheckCircle from "~frontend/components/icons/CheckCircle";
import { SpinningCircle } from "~frontend/components/icons/SpinningCircle";
import {
  useCurrentUser,
  useCurrentUserIsStaff,
} from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";

import { COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS } from "../TollFreeRegistration";
import {
  ORG_TYPE_MENU_OPTIONS,
  TCR_VERTICALS,
  TEXTS_PER_MONTH_MENU_OPTIONS,
} from "./constants";

const AUTOSAVE_INTERVAL_MS = 5000;

export const FOCUS_AREAS = _.sortBy(
  Object.keys(TCR_VERTICALS).map(key => ({
    label: (
      <FlexBox direction="column" gap={0.5}>
        <Typography variant="body1Bold" component="div">
          {TCR_VERTICALS[key as keyof typeof TCR_VERTICALS].displayName}
        </Typography>
        <Typography variant="body2" component="div">
          {TCR_VERTICALS[key as keyof typeof TCR_VERTICALS].description}
        </Typography>
      </FlexBox>
    ),
    value: key,
  })),
  "label"
);

const Section = styled(Box)({
  "> div:last-child > .MuiFormControl-root": {
    mb: 0,
  },
});

const InputRow = ({
  children,
}: {
  children: ReactElement | (string | ReactElement)[];
}) => (
  <FlexBox
    gap={4}
    flexWrap="wrap"
    sx={{ "> .MuiFormControl-root": { minWidth: "48%" } }}
    alignItems="center"
  >
    {children}
  </FlexBox>
);

export const InternalRegistrationForm = ({
  registration,
  saveError,
  pauseSaving,
}: {
  registration: GetRegistrationQuery["getRegistration"];
  saveError?: string | null;
  pauseSaving?: boolean;
}): ReactElement => {
  const {
    values,
    setFieldValue,
    resetForm,
    setValues,
    isSubmitting,
    dirty,
    submitForm,
    submitCount,
    errors,
    touched,
  } = useFormikContext<
    UpdateBrandAndCampaignRegistrationInput & {
      orgContactIsPrimaryContact: boolean;
      submitToStaff: boolean;
    }
  >();

  const isBrandReadonly = !!registration.brandRegistration.submittedAt;
  const isCampaignReadonly = !!registration.submittedAt;

  const currentUser = useCurrentUser();
  const isStaff = useCurrentUserIsStaff();
  const subaccountContext = useSubaccount();
  const isProd = import.meta.env.STW_ENV_NAME === "production";

  const parseFullName = (
    fullName: string | null | undefined
  ): { firstName?: string; lastName?: string } => {
    if (!fullName) {
      return { firstName: "", lastName: "" };
    }

    const first = fullName.split(" ")[0];
    const last = fullName.split(" ").slice(1).join(" ");
    return { firstName: first || "", lastName: last || "" };
  };
  const [orgContactName, setOrgContactName] = useState(
    parseFullName(values.organizationContactName)
  );
  const [primaryContactName, setPrimaryContactName] = useState(
    parseFullName(values.primaryContactName)
  );

  const allowTestPresetUse =
    isStaff && !isProd && !isBrandReadonly && !isCampaignReadonly;

  const [testPreset, setTestPreset] = useState<string>("");
  const TEST_PRESETS = [
    {
      label: "Test Preset 1: Political Organization",
      value: "test-preset-1",
      data: {
        organizationName: `${currentUser?.fullName || "Test"}'s Test Organization`,
        displayName: `${currentUser?.fullName || "Test"}'s Test Org`,
        orgType: TenDlcOrgType.Political,
        ein: "00-0000001",
        einIssuingCountry: "US",
        focusArea: "",
        cvToken:
          "cv|1.2|tcr|10dlc|00000000-0000-0000-0000-000000000000|aBcDeFg",
        expectedTexts: TenDlcExpectedTexts.TenKOrFewer,
        desiredAreaCodes: ["123", "456"],
        preferredAreaCode: "615",
        addressOne: "123 Street",
        addressTwo: "Apt 1",
        city: "City",
        state: "AK",
        postalCode: "12345",
        country: "US",
        organizationContactName: currentUser?.fullName || "Test Contact",
        organizationContactEmail: currentUser?.email || "test@scaletowin.com",
        organizationContactPhone: "615-456-7890",
        primaryContactName: "",
        primaryContactPhone: "",
        primaryContactEmail: "",
        orgContactIsPrimaryContact: true,
        website: "https://www.scaletowin.com",
        description: "Texting program for my Political Campaign",
        embeddedPhone: true,
        optoutMessage: "Text STOP to stop receiving messages",
        helpMessage: "Text HELP to receive help",
        termsAndConditionsLink: "scaletowin.com/terms",
        optInConfirmationMessage: "You're signed up to receive texts!",
        acknowledgedCompliance: true,
        createComplianceBundle: true as boolean,
        optInLink: "scaletowin.com/signup",
        privacyPolicyLink: "scaletowin.com/privacy",
        sampleScript1:
          "I'm a script with enough text to be valid. Stop to quit.",
        sampleScript2: "I'm another script. Stop to quit.",
        sampleScript3: "",
        sampleScript4: "",
        additionalContext:
          "This is a test preset for a political organization.",
        usageStartDate: findEarliestStartDate(),
        submitToStaff: false,
        usageTypes: [ComplianceRegistrationUsageType.Texter],
        subaccountId: subaccountContext?.subaccount.id,
      },
    },
    {
      label: "Test Preset 2: Private Organization",
      value: "test-preset-2",
      data: {
        organizationName: `${currentUser?.fullName}'s Test Organization`,
        displayName: `${currentUser?.fullName}'s Test Org`,
        orgType: TenDlcOrgType.Private,
        ein: "00-0000001",
        einIssuingCountry: "US",
        cvToken: "",
        focusArea: "PROFESSIONAL",
        expectedTexts: TenDlcExpectedTexts.TenKOrFewer,
        desiredAreaCodes: ["123", "456"],
        preferredAreaCode: "615",
        addressOne: "123 Street",
        addressTwo: "Apt 1",
        city: "City",
        state: "AK",
        postalCode: "12345",
        country: "US",
        organizationContactName: currentUser?.fullName || "Test Contact",
        organizationContactEmail: currentUser?.email || "test@scaletowin.com",
        organizationContactPhone: "615-456-7890",
        primaryContactName: "",
        primaryContactPhone: "",
        primaryContactEmail: "",
        orgContactIsPrimaryContact: true,
        website: "https://www.scaletowin.com",
        description: "Private organization texting program with more text",
        embeddedPhone: true,
        optoutMessage: "Text STOP to stop",
        helpMessage: "Text HELP for help",
        termsAndConditionsLink: "scaletowin.com/terms",
        optInConfirmationMessage: "You're in!",
        acknowledgedCompliance: true,
        createComplianceBundle: true as boolean,
        optInLink: "scaletowin.com/signup",
        privacyPolicyLink: "scaletowin.com/privacy",
        sampleScript1:
          "I'm a script with enough text to be valid. Stop to quit.",
        sampleScript2: "I'm another script. Stop to quit.",
        sampleScript3: "",
        sampleScript4: "",
        additionalContext: "This is a test preset for a private organization.",
        usageStartDate: findEarliestStartDate(),
        submitToStaff: false,
        usageTypes: [
          ComplianceRegistrationUsageType.Texter,
          ComplianceRegistrationUsageType.Dialer,
        ],
        subaccountId: subaccountContext?.subaccount.id,
      },
    },
  ];

  useEffect(() => {
    const id = setInterval(async () => {
      if (isSubmitting || !dirty || pauseSaving || values.submitToStaff) {
        return;
      }

      await submitForm();
    }, AUTOSAVE_INTERVAL_MS);

    return () => {
      clearInterval(id);
    };
  }, [isSubmitting, dirty, submitForm, pauseSaving, values.submitToStaff]);

  const renderAutoSaveStatus = () => {
    if (isCampaignReadonly || pauseSaving) {
      return null;
    }

    let icon = <CheckCircle sx={{ height: 20 }} />;
    let label = "Saved!";

    if (isSubmitting) {
      icon = <SpinningCircle />;
      label = "Saving...";
    } else if (saveError) {
      icon = <Alert sx={{ height: 20 }} />;
      label = "Error Saving";
    }

    return (
      <FlexBox direction="column" gap={1}>
        <FlexBox alignItems="center" gap={1} justifyContent="end">
          {icon}
          <Typography
            variant="body2Bold"
            sx={{ color: saveError && !isSubmitting ? palette.red : undefined }}
          >
            {label}
          </Typography>
        </FlexBox>
        {saveError && !isSubmitting ? (
          <Typography variant="body3">
            Check your network settings and contact support if the problem
            persists.
          </Typography>
        ) : null}
      </FlexBox>
    );
  };

  return (
    <>
      <RouteLeavingGuard when={dirty} />
      <FlexBox justifyContent="space-between" alignItems="center">
        <FlexBox
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%", pb: 1 }}
        >
          <PageHeader>10DLC Registration</PageHeader>
          <Box>{renderAutoSaveStatus()}</Box>
        </FlexBox>
      </FlexBox>

      <AccentBox sx={{ px: 10, py: 5 }}>
        <Form>
          <FlexBox direction="column">
            <Section>
              <InputRow>
                <Box>
                  <Typography variant="h2Bold" component="div" sx={{ pb: 1 }}>
                    Where will you use your new 10DLC numbers?
                  </Typography>
                  <FormGroup row>
                    {COMPLIANCE_REGISTRATION_USAGE_TYPE_OPTIONS.map(option => (
                      <FormControlLabel
                        key={option.label}
                        disabled={isCampaignReadonly}
                        control={
                          <Checkbox
                            disabled={isCampaignReadonly}
                            name={`usageType-${option.value}`}
                            checked={values.usageTypes?.includes(option.value)}
                            onChange={async e => {
                              if (e.target.checked) {
                                await setFieldValue("usageTypes", [
                                  ...(values.usageTypes || []),
                                  option.value,
                                ]);
                              } else {
                                await setFieldValue(
                                  "usageTypes",
                                  values.usageTypes?.filter(
                                    type => type !== option.value
                                  )
                                );
                              }
                            }}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </FormGroup>
                  {touched.usageTypes && errors.usageTypes && (
                    <Typography sx={{ color: palette.red }}>
                      {errors.usageTypes as string}
                    </Typography>
                  )}
                </Box>
              </InputRow>

              <Divider sx={{ pt: 2, mb: 3 }} />

              <FlexBox justifyContent="space-between">
                <Typography variant="h2Bold" component="div" sx={{ pb: 3 }}>
                  Your Organization
                </Typography>
                {allowTestPresetUse ? (
                  <Box sx={{ width: "300px" }}>
                    <Select
                      fullWidth
                      label="Test Presets"
                      value={testPreset}
                      onChange={async e => {
                        const selectedPreset = TEST_PRESETS.find(
                          preset => preset.value === e.target.value
                        );
                        if (selectedPreset?.data) {
                          setTestPreset(e.target.value);
                          await setValues({
                            ...selectedPreset.data,
                            subaccountId: subaccountContext!.subaccount.id, // if we know we're staff, subaccountContext will be defined
                            campaignRegistrationId:
                              values.campaignRegistrationId,
                          });
                        }
                      }}
                    >
                      <MenuItem value="">Select a test preset</MenuItem>
                      {TEST_PRESETS.map(preset => (
                        <MenuItem key={preset.value} value={preset.value}>
                          {preset.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                ) : (
                  <div />
                )}
              </FlexBox>

              <InputRow>
                <FormikTextField
                  label="Organization Name"
                  name="organizationName"
                  disabled={isBrandReadonly}
                  fast
                />

                <FormikSelect
                  label="Organization Type"
                  name="orgType"
                  disabled={isBrandReadonly}
                  fast
                >
                  {ORG_TYPE_MENU_OPTIONS.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </InputRow>
              <InputRow>
                <FormikTextField
                  label="Doing Business As (DBA) (optional)"
                  name="displayName"
                  disabled={isBrandReadonly}
                  fast
                />
                <FormikTextField
                  label="EIN"
                  name="ein"
                  onBlur={async e => {
                    const numsOnly = e.target.value.replace("-", "");
                    if (numsOnly.length > 0) {
                      await setFieldValue(
                        "ein",
                        `${numsOnly.slice(0, 2)}-${numsOnly.slice(2)}`
                      );
                    }
                  }}
                  disabled={isBrandReadonly}
                  fast
                />
              </InputRow>
              {values.orgType === TenDlcOrgType.Political ? (
                <InputRow>
                  <FormikTextField
                    label="Campaign Verify Token"
                    name="cvToken"
                    onBlur={async e => {
                      const newValue = e.target.value.trim();
                      await setFieldValue("cvToken", newValue);
                    }}
                    disabled={isBrandReadonly}
                    fast
                    multiline
                    minRows={3}
                  />
                  <Typography
                    component="div"
                    sx={{
                      width: "48%",
                    }}
                  >
                    527s (political committees) must have an identity token from
                    Campaign Verify to apply.{" "}
                    <ExternalLink href="https://support.scaletowin.com/campaign-verify-registration-guide">
                      Click here to learn more about how to get verified through
                      Campaign Verify.
                    </ExternalLink>
                  </Typography>
                </InputRow>
              ) : (
                ""
              )}
              <InputRow>
                <FormikTextField
                  label="Street One"
                  name="addressOne"
                  disabled={isBrandReadonly}
                  fast
                />
                <FormikTextField
                  label="Street Two (optional)"
                  name="addressTwo"
                  disabled={isBrandReadonly}
                  fast
                />
              </InputRow>

              <FlexBox gap={4} sx={{ width: "100%" }}>
                <FormikTextField
                  label="City"
                  name="city"
                  fast
                  fullWidth
                  sx={{ flexGrow: 1 }}
                  disabled={isBrandReadonly}
                />
                <FormikSearchableSelect
                  label="State"
                  name="state"
                  fast
                  options={STATE_MENU_OPTIONS.map(option => {
                    return { label: option.label, id: option.value };
                  })}
                  fullWidth
                  sx={{ flexGrow: 1 }}
                  disabled={isBrandReadonly}
                />
                <FormikTextField
                  label="Postal Code"
                  name="postalCode"
                  fast
                  fullWidth
                  sx={{ flexGrow: 1 }}
                  disabled={isBrandReadonly}
                />
              </FlexBox>
            </Section>

            <Divider sx={{ pt: 2, mb: 3 }} />

            <Section>
              <Typography variant="h2Bold">Your Contact Information</Typography>
              <Typography variant="h2" component="div" sx={{ pt: 1, pb: 3 }}>
                For fastest approval, share a contact's info with an email
                address that matches your organization's website.
              </Typography>
              <InputRow>
                <FormikTextField
                  label="Organization Contact First Name"
                  name="organizationContactName"
                  disabled={isBrandReadonly}
                  fast
                  value={orgContactName.firstName}
                  onChange={async e => {
                    const newFirstName = e.target.value;
                    setOrgContactName({
                      ...orgContactName,
                      firstName: newFirstName,
                    });
                    await setFieldValue(
                      "organizationContactName",
                      [newFirstName, orgContactName.lastName].join(" ")
                    );
                  }}
                  customError={
                    !orgContactName.firstName && submitCount > 0
                      ? { errorText: "First name is required" }
                      : { errorText: undefined }
                  }
                />
                <FormikTextField
                  label="Organization Contact Last Name"
                  name="organizationContactName"
                  disabled={isBrandReadonly}
                  fast
                  value={orgContactName.lastName}
                  onChange={async e => {
                    const newLastName = e.target.value;
                    setOrgContactName({
                      ...orgContactName,
                      lastName: newLastName,
                    });
                    await setFieldValue(
                      "organizationContactName",
                      [orgContactName.firstName, newLastName].join(" ")
                    );
                  }}
                  customError={
                    !orgContactName.lastName && submitCount > 0
                      ? { errorText: "Last name is required" }
                      : { errorText: undefined }
                  }
                />
              </InputRow>
              <InputRow>
                <FormikTextField
                  label="Organization Contact Email"
                  name="organizationContactEmail"
                  type="email"
                  disabled={isBrandReadonly}
                  fast
                />
                <FormikTextField
                  label="Organization Contact Phone Number"
                  name="organizationContactPhone"
                  disabled={isBrandReadonly}
                  fast
                />
                <div style={{ width: "48%" }} />
              </InputRow>
              <Typography variant="h2">
                Is the organization contact the person Scale to Win should reach
                out to with questions about this registration?
              </Typography>
              <RadioGroup
                row={true}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
                value={values.orgContactIsPrimaryContact}
                onChange={async e => {
                  await setFieldValue(
                    "orgContactIsPrimaryContact",
                    e.target.value === "true"
                  );
                }}
                name="orgContactIsPrimaryContact"
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={isBrandReadonly}
                />

                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={isBrandReadonly}
                />
              </RadioGroup>

              {values.orgContactIsPrimaryContact ? null : (
                <>
                  <InputRow>
                    <FormikTextField
                      label="Primary Contact First Name"
                      name="primaryContactName"
                      disabled={isBrandReadonly}
                      fast
                      value={primaryContactName.firstName}
                      onChange={async e => {
                        const newFirstName = e.target.value;
                        setPrimaryContactName({
                          ...primaryContactName,
                          firstName: newFirstName,
                        });
                        await setFieldValue(
                          "primaryContactName",
                          [newFirstName, primaryContactName.lastName].join(" ")
                        );
                      }}
                      customError={
                        !primaryContactName.firstName && submitCount > 0
                          ? { errorText: "First name is required" }
                          : { errorText: undefined }
                      }
                    />
                    <FormikTextField
                      label="Primary Contact Last Name"
                      name="primaryContactName"
                      disabled={isBrandReadonly}
                      fast
                      value={primaryContactName.lastName}
                      onChange={async e => {
                        const newLastName = e.target.value;
                        setPrimaryContactName({
                          ...primaryContactName,
                          lastName: newLastName,
                        });
                        await setFieldValue(
                          "primaryContactName",
                          [primaryContactName.firstName, newLastName].join(" ")
                        );
                      }}
                      customError={
                        !primaryContactName.lastName && submitCount > 0
                          ? { errorText: "Last name is required" }
                          : { errorText: undefined }
                      }
                    />
                  </InputRow>
                  <InputRow>
                    <FormikTextField
                      label="Primary Contact Email"
                      name="primaryContactEmail"
                      type="email"
                      disabled={isBrandReadonly}
                      fast
                    />
                    <FormikTextField
                      label="Primary Contact Phone Number"
                      name="primaryContactPhone"
                      disabled={isBrandReadonly}
                      fast
                    />
                    <div style={{ width: "48%" }} />
                  </InputRow>
                </>
              )}
            </Section>

            <Divider sx={{ pt: 2, mb: 3 }} />

            <Box>
              <Typography variant="h2Bold" component="div" sx={{ pb: 1 }}>
                Your Texting Program
              </Typography>
              <InputRow>
                <FormikSelect
                  label="Preferred Area Code"
                  name="preferredAreaCode"
                  withNoneOption
                  noneOptionLabel="Select preferred area code..."
                  disabled={isBrandReadonly}
                  fast
                >
                  {STATES_WITH_AREA_CODES.flatMap(state => [
                    <ListSubheader>{state.name}</ListSubheader>,
                    ...state.areaCodes.map(areaCode => (
                      <MenuItem value={areaCode}>{areaCode}</MenuItem>
                    )),
                  ])}
                </FormikSelect>
                <FormikSelect
                  label="Alternate Area Codes"
                  name="desiredAreaCodes"
                  overrideFormikProps={field => ({ ...field, multiple: true })}
                  disabled={isBrandReadonly}
                  fast
                >
                  {STATES_WITH_AREA_CODES.flatMap(state => [
                    <ListSubheader>{state.name}</ListSubheader>,
                    ...state.areaCodes.map(areaCode => (
                      <MenuItem value={areaCode}>{areaCode}</MenuItem>
                    )),
                  ])}
                </FormikSelect>
              </InputRow>
              <InputRow>
                <FormikSelect
                  label="Expected Texts per Month"
                  name="expectedTexts"
                  withNoneOption
                  noneOptionLabel="Expected texts..."
                  disabled={isBrandReadonly}
                  fast
                >
                  {TEXTS_PER_MONTH_MENU_OPTIONS.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </FormikSelect>
                <FormikTextField
                  label="Your Website"
                  name="website"
                  disabled={isBrandReadonly}
                  fast
                />
              </InputRow>

              {values.orgType === TenDlcOrgType.Private ? (
                <InputRow>
                  <FormikSelect
                    renderValue={value => {
                      const focusArea =
                        TCR_VERTICALS[value as keyof typeof TCR_VERTICALS];
                      return focusArea ? focusArea.displayName : "";
                    }}
                    label="Focus Area"
                    name="focusArea"
                    disabled={isBrandReadonly}
                    fast
                  >
                    {FOCUS_AREAS.map(area => (
                      <MenuItem key={area.value} value={area.value}>
                        {area.label}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                  <Box sx={{ width: "49%" }} />
                </InputRow>
              ) : (
                ""
              )}

              <FormControlLabel
                sx={{
                  color: errors.acknowledgedCompliance
                    ? palette.red
                    : undefined,
                }}
                control={
                  <Checkbox
                    name="acknowledgedCompliance"
                    value={values.acknowledgedCompliance}
                    checked={!!values.acknowledgedCompliance}
                    onChange={async e => {
                      await setFieldValue(
                        "acknowledgedCompliance",
                        !!e.target.checked
                      );
                    }}
                    disabled={isCampaignReadonly}
                  />
                }
                label={
                  <Typography variant="h2Bold" sx={{ pt: 1 }}>
                    By checking this box, you acknowledge that you are required
                    to have a text message opt in form, website terms &
                    conditions and privacy policy that adheres to, or is
                    substantially similar to the 10DLC registration
                    requirements.
                  </Typography>
                }
              />
              <Typography variant="h2" sx={{ pt: 2 }}>
                Would you like Scale to Win to generate sample sign-up, terms &
                conditions and privacy policy pages that meet these requirements
                for you and submit them on your behalf?
              </Typography>
              <RadioGroup
                row={true}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
                value={values.createComplianceBundle}
                onChange={async e => {
                  await setFieldValue(
                    "createComplianceBundle",
                    e.target.value === "true"
                  );
                }}
                name="createComplianceBundle"
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                  disabled={isCampaignReadonly}
                />

                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                  disabled={isCampaignReadonly}
                />
              </RadioGroup>
            </Box>
            {!values.createComplianceBundle && (
              <Box>
                <CollapsibleDrawer
                  title="10DLC Registration Requirements"
                  startOpen
                  variant="slim"
                  sxHeader={{
                    backgroundColor: palette.midGray,
                    py: 0.5,
                    px: 2,
                  }}
                  sxHeading={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: palette.navy,
                  }}
                >
                  <FlexBox
                    direction="column"
                    gap={3}
                    sx={{
                      border: `1px solid ${palette.midGray}`,
                      background: palette.lightestGray,
                      p: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h2Bold"
                        component="div"
                        sx={{ pb: 1, fontWeight: 600 }}
                      >
                        Website Requirements
                      </Typography>

                      <Typography>Your website:</Typography>
                      <ul>
                        <li>Must be active (publicly accessible).</li>
                        <li>Must have content.</li>
                        <li>Must have a privacy policy.</li>
                        <li>Must have terms and conditions.</li>
                      </ul>

                      <Typography>
                        If contacts can sign up for texts from you on a website,{" "}
                        <u>it must have these elements</u>.
                      </Typography>
                      <ul>
                        <li>
                          An optional field for users to enter their phone
                          number
                        </li>
                        <li>
                          Explanatory language beneath, i.e. “By providing your
                          phone number you are agreeing to receive texts at a
                          maximum of X times a WEEK/DAY/MONTH from ORGANIZATION.
                          Message and data rates may apply. Text HELP for more
                          information. Text STOP to stop receiving messages.
                          Terms [LINK] and Privacy [LINK]
                        </li>
                      </ul>
                      <ExternalLink href="https://support.scaletowin.com/10dlc-registration-requirements">
                        Read more about carrier requirements here.
                      </ExternalLink>
                    </Box>
                    <Box>
                      <Typography
                        variant="h2Bold"
                        component="div"
                        sx={{ fontWeight: 600 }}
                      >
                        Terms and Conditions Requirements
                      </Typography>

                      <ul>
                        <li>Must contain the sending organization's name.</li>
                        <li>
                          Must specify the types of messages being sent. If
                          donations are included, must state, “donation asks”.
                        </li>
                        <li>
                          Must disclosure approximate message frequency (Msg
                          frequency varies, 2/msgs per week, etc.)
                        </li>
                        <li>
                          Must include a "Message and data rates may apply"
                          disclosure
                        </li>
                        <li>
                          Must include HELP information (text HELP for help,
                          email or phone number for support)
                        </li>
                        <li>
                          Must include STOP/opt-out information (text STOP to
                          stop)
                        </li>
                        <li>Must include a link to the Privacy Policy</li>
                      </ul>
                      <FormikTextField
                        fullWidth
                        label="Your Terms and Conditions Page"
                        name="termsAndConditionsLink"
                        disabled={isCampaignReadonly}
                        fast
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="h2Bold"
                        component="div"
                        sx={{ pb: 1, fontWeight: 600 }}
                      >
                        Privacy Policy Requirements
                      </Typography>
                      <Typography>
                        Carriers are strict about how mobile user data is
                        handled.
                      </Typography>
                      <ul>
                        <li>
                          You can only share mobile data for essential services
                          or if required by law.
                        </li>
                        <li>
                          You cannot share mobile data with affiliates or
                          similar campaigns.
                        </li>
                      </ul>
                      <FlexBox direction="column" gap={2}>
                        <Typography component="div">
                          Your privacy policy must clearly state this.
                        </Typography>
                        <Typography component="div">
                          You can make this clear on your privacy policy with
                          language such as:
                        </Typography>
                        <Typography component="div">
                          Text messaging originator opt in data and consent will
                          not be shared with any third parties, provided that
                          the foregoing does not apply to sharing
                        </Typography>
                        <Typography component="div">
                          (1) with vendors, consultants and other service
                          providers who need access to such information to carry
                          out work on our behalf (and who will not use such
                          information for their own purposes);
                        </Typography>
                        <Typography component="div">
                          (2) if we believe disclosure is required by any
                          applicable law, rule, or regulation or to comply with
                          law enforcement or legal process.
                        </Typography>
                        <FormikTextField
                          fullWidth
                          label="Your Privacy Policy Page"
                          name="privacyPolicyLink"
                          disabled={isCampaignReadonly}
                          fast
                        />
                      </FlexBox>
                    </Box>

                    <Box>
                      <Typography
                        variant="h2Bold"
                        component="div"
                        sx={{ fontWeight: 600 }}
                      >
                        Opt-In Form Requirements
                      </Typography>

                      <Box>
                        <ul>
                          <li>
                            Must have an optional field to enter a phone number.
                          </li>
                          <li>
                            Cannot have a pre-checked checkbox signing users up
                            for texts.
                          </li>
                          <li>
                            Must include a call-to-action explaining to the user
                            they are signing up for texts.
                          </li>
                          <li>
                            Must link to the terms and conditions and privacy
                            policy.
                          </li>
                          <li>
                            Eg. "By submitting this form and signing up for
                            texts, you consent to receive campaign updates and
                            donation text messages from [Org Name] at the number
                            provided, including messages sent by autodialer. Msg
                            & data rates may apply. Msg frequency varies.
                            Unsubscribe at any time by replying STOP. Reply HELP
                            for help. Privacy Policy [link] & Terms [link]"
                          </li>
                        </ul>

                        <FormikTextField
                          fullWidth
                          label="Your Opt-In Form Page"
                          name="optInLink"
                          disabled={isCampaignReadonly}
                          fast
                        />
                      </Box>
                    </Box>
                  </FlexBox>
                </CollapsibleDrawer>
              </Box>
            )}

            <Divider sx={{ pt: 2, mb: 3 }} />

            <Box>
              <Typography variant="h2Bold" sx={{ fontWeight: 600 }}>
                Anticipated Date of Use
              </Typography>
              <Typography variant="h2" component="div" sx={{ py: 1 }}>
                When will you start using this number?
              </Typography>
              <InputRow>
                <FormikDatePicker
                  label="Requested Start Date"
                  name="usageStartDate"
                  fast
                  minDate={findEarliestStartDate()}
                  inputLabelProps={{
                    sx: { marginBottom: "-.25rem", marginTop: ".5rem" },
                  }}
                  helperTextProps={{
                    sx: { marginLeft: "14px", fontSize: ".75xem" },
                  }}
                  helperText="Start date must be 3 or more business days in the future"
                  fullWidth
                />
              </InputRow>
            </Box>

            <Divider sx={{ pt: 2, mb: 3 }} />

            <Box>
              <Typography variant="h2Bold" sx={{ fontWeight: 600 }}>
                Program Description
              </Typography>
              <Typography variant="h2" component="div" sx={{ py: 1 }}>
                Describe the kinds of texts you'll send, i.e. volunteer
                recruitment, fundraising, GOTV.
              </Typography>
              <InputRow>
                <FormikTextField
                  label="Description"
                  name="description"
                  disabled={isCampaignReadonly}
                  fast
                  multiline
                  fullWidth
                />
              </InputRow>

              <FormControlLabel
                control={
                  <Checkbox
                    name="embeddedPhone"
                    value={values.embeddedPhone}
                    checked={!!values.embeddedPhone}
                    onChange={async e => {
                      await setFieldValue("embeddedPhone", !!e.target.checked);
                    }}
                    disabled={isCampaignReadonly}
                  />
                }
                label="Check if you will include phone numbers in your texts, i.e. contact the voter registration hotline for more information at (XXX)-XXX-XXXX"
              />
            </Box>

            <Divider sx={{ pt: 2, mb: 3 }} />

            <FlexBox direction="column" gap={2}>
              <Box>
                <Typography
                  variant="h2Bold"
                  component="div"
                  sx={{ fontWeight: 600, pb: 1 }}
                >
                  Example Scripts
                </Typography>
                <Typography variant="h2">
                  Provide at least two example text messages. Your samples must
                  include the sender org name and opt out language, i.e. Stop to
                  opt out
                </Typography>
              </Box>
              {_.times(4, i => (
                <FormikTextField
                  key={`sampleScript${i + 1}`}
                  label={`Sample Script ${i + 1}${i > 1 ? " (optional)" : ""}`}
                  name={`sampleScript${i + 1}`}
                  multiline
                  minRows={3}
                  maxRows={10}
                  disabled={isCampaignReadonly}
                  fast
                  bottomLabel="Sample scripts must be between 20 and 1024 characters"
                />
              ))}

              <FormikTextField
                multiline
                label="Any additional context you would like to share? (optional)"
                name="additionalContext"
                disabled={isCampaignReadonly}
                fast
              />
            </FlexBox>

            {!isCampaignReadonly && (
              <FlexBox gap={2} justifyContent="flex-end">
                <Button variant="link" onClick={() => resetForm()}>
                  Cancel
                </Button>

                {subaccountContext?.subaccount ? (
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      await setFieldValue("submitToStaff", false);
                      await submitForm();
                    }}
                  >
                    Save as Draft
                  </Button>
                ) : (
                  <div />
                )}

                <Button
                  variant="contained"
                  loading={isSubmitting}
                  onClick={async () => {
                    await setFieldValue("submitToStaff", true);
                    await submitForm();
                  }}
                >
                  Submit for Review
                </Button>
              </FlexBox>
            )}
          </FlexBox>
        </Form>
      </AccentBox>
    </>
  );
};
